import { Flex } from 'antd';
import React from 'react';
import cn from 'classnames';

import type { ModalManagerProvidedProps } from 'admin/components/common/ModalManager';
import type { InspectorContextType } from 'admin/components/pages/Story/CardEditor/Inspector/InspectorContext';
import { OTHER_PROPS } from 'common/constants/component-props';
import { MODAL } from 'admin/constants/common';
import { useAdminSelector } from 'admin/reducers';
import { Select, TextField } from 'admin/components/common/Form';
import { TogglePure as Toggle } from 'admin/components/common/Form/Toggle';
import Button from 'admin/components/common/Button';
import { Modal, ModalBody, ModalFooter } from 'admin/components/common/Modal';
import { selectEditableElement } from 'admin/reducers/card-editor-extra/selectors';
import { getPFCompPropName } from 'admin/components/pages/Story/CardEditor/Inspector/PropField';
import {
	createDefaultConfig,
	NumberFormatType,
	type NumberFormatConfig,
	type Delimiter,
	UnitPosition,
} from 'client/components/common/BuildingBlocks/Text/number-format';
import { selectCurrentPlatform, selectCurrentState } from 'admin/reducers/card-editor/selectors';
import { useTheme } from 'admin/components/common/Theme';
import Text from 'admin/components/common/Text';
import useNumberFormatter from 'client/components/common/BuildingBlocks/Text/useNumberFormatter';
import t from 'utils/translate';
import css from './EditNumberFormat.scss';

export type EditNumberFormatModalData = {
	onSave: InspectorContextType['onUpdate'];
};

type ModalProps = ModalManagerProvidedProps<MODAL.EDIT_NUMBER_FORMAT>;

const texts = {
	title: 'Number format',
	unitLabel: 'Unit',
	unitPositionLabel: 'Unit position',
	precisionLabel: 'Precision',
	delimiterLabel: 'Delimiter',
	compactLabel: 'Compact',
};

const ModalContent: React.FC<ModalProps> = props => {
	const element = useAdminSelector(selectEditableElement);
	const currentState = useAdminSelector(selectCurrentState);
	const currentMediaQuery = useAdminSelector(selectCurrentPlatform);
	const { theme } = useTheme();

	const [config, setConfig] = React.useState<NumberFormatConfig>(
		() =>
			element.uiConfig.componentProps.other.numberFormatConfig ??
			createDefaultConfig(element.uiConfig.componentProps.other.numberFormatType!)
	);
	const example = useNumberFormatter('1000000', config);

	const handleSave = () => {
		(props.data satisfies EditNumberFormatModalData).onSave({
			name: getPFCompPropName(
				OTHER_PROPS.text.numberFormatConfig,
				'other'
			)({ path: element.path, currentState, currentMediaQuery }),
			value: config,
		});

		props.close();
	};

	const renderContent = () => {
		const isCompact =
			config.type === NumberFormatType.Compact || (config.type === NumberFormatType.Custom && config.compact);

		switch (config.type) {
			case NumberFormatType.Custom: {
				const delimiterOptions: { [K in Delimiter]: { label: string; value: K } }[Delimiter][] = [
					{ label: 'Comma', value: 'comma' },
					{ label: 'Dot', value: 'dot' },
					{ label: 'Space', value: 'space' },
				];
				const unitPositionOptions: { [K in UnitPosition]: { label: string; value: K } }[UnitPosition][] = [
					{ label: 'Before', value: 'before' },
					{ label: 'After', value: 'after' },
				];

				return (
					<>
						<Text className={css.example}>{example}</Text>
						<Select
							disabled={isCompact}
							className={css.select}
							label={{ children: texts.delimiterLabel, theme }}
							value={config.delimiter}
							options={delimiterOptions}
							theme={theme}
							eventListeners={{
								onChange: value => {
									setConfig(c => ({ ...c, delimiter: value }));
								},
							}}
							menuPlacement="top"
							maxMenuHeight={200}
						/>
						<TextField
							className={css.textInput}
							label={texts.precisionLabel}
							isLabelUppercase={false}
							isRequired
							value={config.precision.toString()}
							placeholder="0"
							type="number"
							theme={theme}
							onChange={e => {
								const v = Math.min(+e.target.value > 0 ? +e.target.value : 0, 10);
								setConfig(c => ({ ...c, precision: v.toString() }));
							}}
						/>
						<div className={css.inputsRow}>
							<TextField
								className={css.textInput}
								label={texts.unitLabel}
								isLabelUppercase={false}
								isRequired
								value={config.unit.toString()}
								placeholder="$"
								theme={theme}
								onChange={e => {
									setConfig(c => ({ ...c, unit: e.target.value }));
								}}
							/>
							<Select
								className={cn(css.select, !config.unit && css.disabled)}
								label={{ children: texts.unitPositionLabel, theme }}
								value={config.unitPosition}
								options={unitPositionOptions}
								theme={theme}
								eventListeners={{
									onChange: value => {
										setConfig(c => ({ ...c, unitPosition: value }));
									},
								}}
								menuPlacement="top"
								maxMenuHeight={200}
							/>
						</div>
						<Toggle
							label={{ children: texts.compactLabel, theme }}
							checked={config.compact}
							switchProps={{
								onChange: value => {
									setConfig(c => ({ ...c, compact: value }));
								},
							}}
						/>
					</>
				);
			}

			default:
				return null;
		}
	};

	return (
		<>
			<ModalBody>
				<Flex vertical gap="middle">
					{renderContent()}
				</Flex>
			</ModalBody>
			<ModalFooter>
				<Button view="primary" onClick={handleSave}>
					{t('story.propSettingsModal.save')}
				</Button>
				<Button view="secondary-gray" onClick={props.close}>
					{t('story.propSettingsModal.discard')}
				</Button>
			</ModalFooter>
		</>
	);
};

const EditNumberFormatModal: React.FC<ModalProps> = props => {
	return (
		<Modal
			theme="dark"
			maskColor="black"
			open={props.open}
			destroyOnHidden
			width={640}
			transitionName=""
			title={<Text size={Text.size.subtitle} weight={Text.weight.semibold} compact text={texts.title} />}
		>
			<ModalContent {...props} />
		</Modal>
	);
};

export default EditNumberFormatModal;
